import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PreloadLink from './PreloadLink';
import NotificationBell from './NotificationBell';
import Avatar from './Avatar';
import FABMenu from './FABMenu';
import { triggerHaptic, HapticPatterns } from '../utils/haptics';
import logo from '../assets/logo.svg';
import {
  HomeIcon,
  MapPinIcon,
  CalendarIcon,
  TrophyIcon,
  ClipboardIcon,
  UserIcon,
  ImageIcon,
  ClockIcon,
  LockIcon,
  SettingsIcon,
  LayersIcon,
  LogOutIcon,
  PlusIcon,
  TargetIcon,
  PartyIcon,
  MedalIcon,
  StarIcon,
  BadgeIcon,
  HelpCircleIcon,
  HeartIcon
} from '../icons';

function Navbar({ user, onLogout, onChangeLanguage, currentLanguage }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    triggerHaptic(HapticPatterns.light);
  };

  const handleMenuToggle = () => {
    triggerHaptic(HapticPatterns.medium);
    setMenuOpen(!menuOpen);
  };

  const handleFabClick = () => {
    triggerHaptic(HapticPatterns.medium);
    setFabMenuOpen(!fabMenuOpen);
  };

  return (
    <>
      {/* Top Navbar - Desktop & Mobile */}
      <nav className="top-navbar">
        <div className="top-navbar-content">
          <PreloadLink to="/" className="navbar-logo" onClick={handleNavClick}>
            <img src={logo} alt="Mandrillapp" className="navbar-logo-image" style={{ height: '32px', width: '32px', marginRight: '8px' }} />
            <span className="navbar-logo-text">Mandrillapp</span>
          </PreloadLink>

          <div className="top-navbar-actions">
            <NotificationBell />
            <button
              className="hamburger-menu"
              onClick={handleMenuToggle}
              aria-label="Toggle menu"
            >
              <span className={menuOpen ? 'hamburger-line open' : 'hamburger-line'}></span>
              <span className={menuOpen ? 'hamburger-line open' : 'hamburger-line'}></span>
              <span className={menuOpen ? 'hamburger-line open' : 'hamburger-line'}></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hamburger Sidebar Menu */}
      <div className={`sidebar-menu ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-overlay" onClick={() => setMenuOpen(false)}></div>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="sidebar-header-left">
              <img src={logo} alt="Logo" className="sidebar-logo" />
              <h3>Mandrillapp</h3>
            </div>
            <button className="sidebar-close" onClick={() => setMenuOpen(false)}>
              <span className="close-line"></span>
              <span className="close-line"></span>
              <span className="close-line"></span>
            </button>
          </div>

          {/* User Profile Section */}
          <div className="sidebar-user-profile" onClick={() => { navigate('/profile'); setMenuOpen(false); handleNavClick(); }}>
            <Avatar
              username={user?.username || user?.full_name || 'User'}
              avatarUrl={user?.avatar_url}
              rank={user?.rank}
              size="md"
              className="sidebar-user-avatar"
            />
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.full_name || user?.username || 'User'}</div>
              <div className="sidebar-user-action">{t('view_profile')}</div>
            </div>
          </div>

          <nav className="sidebar-nav">
            {/* ACTIVITIES Section */}
            <div className="sidebar-section-label">ACTIVITIES</div>
            <PreloadLink to="/events" className={`sidebar-link ${isActive('/events') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <PartyIcon className="sidebar-icon" width="16" height="16" />
              {t('events')}
            </PreloadLink>
            <PreloadLink to="/calendar" className={`sidebar-link ${isActive('/calendar') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <CalendarIcon className="sidebar-icon" width="16" height="16" />
              {t('calendar')}
            </PreloadLink>
            <PreloadLink to="/wishlist" className={`sidebar-link ${isActive('/wishlist') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <HeartIcon className="sidebar-icon" width="16" height="16" />
              {t('wishlist')}
            </PreloadLink>
            <PreloadLink to="/locations" className={`sidebar-link ${isActive('/locations') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <MapPinIcon className="sidebar-icon" width="16" height="16" />
              {t('locations')}
            </PreloadLink>
            <PreloadLink to="/challenges" className={`sidebar-link ${isActive('/challenges') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <TargetIcon className="sidebar-icon" width="16" height="16" />
              {t('challenges')}
            </PreloadLink>
            <PreloadLink to="/media" className={`sidebar-link ${isActive('/media') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <ImageIcon className="sidebar-icon" width="16" height="16" />
              {t('media_gallery')}
            </PreloadLink>

            {/* COMMUNITY Section */}
            <div className="sidebar-section-label">COMMUNITY</div>
            <PreloadLink to="/leaderboard" className={`sidebar-link ${isActive('/leaderboard') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <TrophyIcon className="sidebar-icon" width="16" height="16" />
              {t('leaderboard')}
            </PreloadLink>
            <PreloadLink to="/badges" className={`sidebar-link ${isActive('/badges') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <BadgeIcon className="sidebar-icon" width="16" height="16" />
              {t('badges')}
            </PreloadLink>
            <PreloadLink to="/users" className={`sidebar-link ${isActive('/users') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <UserIcon className="sidebar-icon" width="16" height="16" />
              {t('users')}
            </PreloadLink>
            <PreloadLink to="/statistics" className={`sidebar-link ${isActive('/statistics') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <ClipboardIcon className="sidebar-icon" width="16" height="16" />
              {t('statistics')}
            </PreloadLink>
            <PreloadLink to="/point-history" className={`sidebar-link ${isActive('/point-history') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <ClockIcon className="sidebar-icon" width="16" height="16" />
              {t('point_history')}
            </PreloadLink>
            <PreloadLink to="/hall-of-fame" className={`sidebar-link ${isActive('/hall-of-fame') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <StarIcon className="sidebar-icon" width="16" height="16" />
              {t('hall_of_fame')}
            </PreloadLink>

            {/* SYSTEM Section */}
            <div className="sidebar-section-label">SYSTEM</div>
            <PreloadLink to="/settings" className={`sidebar-link ${isActive('/settings') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <SettingsIcon className="sidebar-icon" width="16" height="16" />
              {t('settings')}
            </PreloadLink>
            <PreloadLink to="/support" className={`sidebar-link ${isActive('/support') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
              <HelpCircleIcon className="sidebar-icon" width="16" height="16" />
              {t('support')}
            </PreloadLink>
            {user?.is_admin && (
              <PreloadLink to="/admin" className={`sidebar-link ${isActive('/admin') ? 'active' : ''}`} onClick={() => { setMenuOpen(false); handleNavClick(); }}>
                <LayersIcon className="sidebar-icon" width="16" height="16" />
                {t('admin')}
              </PreloadLink>
            )}

            <div className="sidebar-divider"></div>

            <button
              className="sidebar-link sidebar-logout"
              onClick={() => {
                onLogout();
                setMenuOpen(false);
                triggerHaptic(HapticPatterns.medium);
              }}
            >
              <LogOutIcon className="sidebar-icon" width="16" height="16" />
              {t('logout')}
            </button>
          </nav>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <nav className="bottom-navbar">
        <PreloadLink to="/" className={`bottom-nav-item ${isActive('/') ? 'active' : ''}`} onClick={handleNavClick} aria-label="Home">
          <HomeIcon width="28" height="28" />
        </PreloadLink>

        <PreloadLink to="/calendar" className={`bottom-nav-item ${isActive('/calendar') ? 'active' : ''}`} onClick={handleNavClick} aria-label="Events">
          <CalendarIcon width="28" height="28" />
        </PreloadLink>

        <button
          className={`bottom-nav-fab ${fabMenuOpen ? 'active' : ''}`}
          onClick={handleFabClick}
          aria-label="Create"
          aria-expanded={fabMenuOpen}
        >
          <PlusIcon width="26" height="26" strokeWidth="3" />
        </button>

        <PreloadLink to="/leaderboard" className={`bottom-nav-item ${isActive('/leaderboard') ? 'active' : ''}`} onClick={handleNavClick} aria-label="Statistics">
          <TrophyIcon width="28" height="28" />
        </PreloadLink>

        <PreloadLink to="/profile" className={`bottom-nav-item ${isActive('/profile') ? 'active' : ''}`} onClick={handleNavClick} aria-label="Profile">
          <UserIcon width="28" height="28" />
        </PreloadLink>
      </nav>

      {/* FAB Menu */}
      <FABMenu isOpen={fabMenuOpen} onClose={() => setFabMenuOpen(false)} />
    </>
  );
}

export default Navbar;

